/**
 * @file AbaJobsByState.jsx
 * @description Public index at /aba-jobs-by-state (page engine, 2026-10-20): every state where ABAcases has
 *   released location pages, highest ABA workforce first, each linking to its state page and its places. The one
 *   page the footer links to, so the footer never grows into a list of cities. Lists come from lib/seoLocations
 *   (released pages only); the workforce line is public BACB data (data/seoAreas.json statesSource), never a
 *   count of ABAcases users.
 * @module components/AbaJobsByState
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { MapPinIcon } from "./Icons";
import PublicFooter from "./PublicFooter";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import HeroAlertsCta from "./HeroAlertsCta";
import { liveStates, LOCATIONS_INDEX_SLUG } from "../lib/seoLocations";
import seoAreas from "../data/seoAreas.json";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function AbaJobsByState({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language?.startsWith("es");
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "ABA Jobs by State: RBT, BCBA and ABA Cases Near You | ABAcases",
    description: "Find ABA cases by state and city. RBT, BCBA, BCaBA and BT opportunities by pay, schedule and location, with free case alerts when one opens near you.",
    canonical: `/${LOCATIONS_INDEX_SLUG}`,
  });
  useSiteSchema();
  useBreadcrumbJsonLd("bc-jsonld-aba-jobs-by-state", [
    { name: "Home", url: "/" },
    { name: "ABA jobs by state", url: `/${LOCATIONS_INDEX_SLUG}` },
  ]);

  const states = liveStates();
  const fmt = new Intl.NumberFormat(isEs ? "es-US" : "en-US");
  const placeName = (p) => (isEs && p.nameEs) || p.area;

  const SeoLink = ({ to, style, children, testId }) => (
    <a href={seoHref(to)} data-testid={testId} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const maxW = 980;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 56px" }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <Breadcrumbs items={[
            { label: t("abaJobsByState.breadcrumbHome"), onClick: () => onNavigate("home") },
            { label: t("abaJobsByState.breadcrumbIndex") },
          ]} />
          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t("abaJobsByState.heroTitle")}
          </h1>
          <p style={{ fontSize: isMobile ? 15.5 : 17, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 680, margin: 0 }}>
            {t("abaJobsByState.heroSubtitle")}
          </p>
          <div style={{ marginTop: 22 }}>
            <HeroAlertsCta label={t("abaJobsByState.ctaButton")} pagePath={`/${LOCATIONS_INDEX_SLUG}/`} pageType="resource" audience="provider" onNavigate={onNavigate} isMobile={isMobile} />
          </div>
        </div>
      </header>

      <main style={{ maxWidth: maxW, margin: "0 auto", padding: isMobile ? "28px 18px 40px" : "40px 48px 56px", display: "grid", gap: 16, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))" }}>
        {states.map((s) => {
          const total = s.code && seoAreas.states[s.code] ? Object.values(seoAreas.states[s.code].bacb).reduce((a, b) => a + b, 0) : null;
          const stateName = isEs ? s.nameEs : s.name;
          return (
            <section key={s.code} data-testid="state-card" aria-labelledby={`state-${s.code}`} style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: isMobile ? "18px 16px" : "22px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
              <h2 id={`state-${s.code}`} style={{ margin: 0, fontSize: 21, fontWeight: 900, color: NAVY, letterSpacing: "-0.02em" }}>
                {s.hub ? <SeoLink to={s.hub.page.slug} style={{ color: NAVY }} testId="state-hub-link">{stateName}</SeoLink> : stateName}
              </h2>
              {total ? <p style={{ margin: 0, fontSize: 14, color: TEXT_MID, lineHeight: 1.55 }}>{t("abaJobsByState.workforce", { count: total, formatted: fmt.format(total), state: stateName })}</p> : null}
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {s.places.map((p) => (
                  <li key={p.areaKey}>
                    <SeoLink to={p.page.slug} testId="state-place-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: INDIGO, background: "#f5f3ff", borderRadius: 999, padding: "6px 12px" }}>
                      <MapPinIcon size={13} color={INDIGO} /> {placeName(p)}
                    </SeoLink>
                  </li>
                ))}
                {s.hub && (
                  <li>
                    <SeoLink to={s.hub.page.slug} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: TEXT_MID, borderRadius: 999, padding: "6px 12px", border: `1px solid ${BORDER}` }}>
                      {t("abaJobsByState.allOfState", { state: stateName })}
                    </SeoLink>
                  </li>
                )}
              </ul>
            </section>
          );
        })}
      </main>

      <section style={{ maxWidth: maxW, margin: "0 auto", padding: isMobile ? "0 18px 40px" : "0 48px 56px" }}>
        <p style={{ margin: "14px 0 0", fontSize: 12, color: TEXT_MID }}>
          {t("abaJobsByState.sourceNote", { date: seoAreas.statesSource.readOn })}
        </p>
      </section>

      <PublicFooter />
    </div>
  );
}
